import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { adminApiRequest } from "../utils/adminApi";

export default function TableBookings({ setIsAnyModalActive }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalConfig, setModalConfig] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch bookings from backend
  const fetchBookings = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);

    try {
      const data = await adminApiRequest({
        script: "get-table-bookings",
      });

      const bookingList = Array.isArray(data?.bookings)
        ? data.bookings
        : Array.isArray(data?.data)
        ? data.data
        : [];

      setBookings(bookingList.sort((a, b) => a.id - b.id));
    } catch (err) {
      console.error("Error fetching bookings:", err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings(true);
    const interval = setInterval(() => fetchBookings(false), 5000);
    return () => clearInterval(interval);
  }, []);

  // Delete booking
  const deleteBooking = async (id) => {
    setActionLoading(true);
    setModalConfig(null);
    if (setIsAnyModalActive) setIsAnyModalActive(false);

    try {
      const data = await adminApiRequest({
        script: "delete-table-booking",
        data: { id },
      });

      if (data.success) {
        fetchBookings(false);
      } else {
        console.error("Delete failed:", data.message);
      }
    } catch (err) {
      console.error("Server error:", err);
    } finally {
      setActionLoading(false);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (booking) => {
    setModalConfig({
      title: "Delete Booking",
      content: (
        <div className="text-center text-base text-gray-700">
          <p>
            Are you sure you want to delete the booking for{" "}
            <strong>
              {booking.first_name} {booking.last_name}
            </strong>
            ?
          </p>
        </div>
      ),
      onConfirm: () => deleteBooking(booking.id),
      type: "CONFIRM_MODAL",
    });

    if (setIsAnyModalActive) setIsAnyModalActive(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeModal = () => {
    setModalConfig(null);
    if (setIsAnyModalActive) setIsAnyModalActive(false);
  };

  return (
    <div className="w-full rounded-2xl bg-white p-4 shadow-sm md:p-6">
      <h2 className="mb-6 text-2xl font-bold text-gray-800">Table Bookings</h2>

      {(loading || actionLoading) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-300 border-t-black"></div>
        </div>
      )}

      {!loading && (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3 font-semibold">S/N</th>
                <th className="px-4 py-3 font-semibold">First Name</th>
                <th className="px-4 py-3 font-semibold">Last Name</th>
                <th className="px-4 py-3 font-semibold">Phone</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Seats</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Time</th>
                <th className="px-4 py-3 font-semibold">Special Request</th>
                <th className="px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 bg-white text-gray-700">
              {bookings.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    No bookings found.
                  </td>
                </tr>
              ) : (
                bookings.map((b, index) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{index + 1}</td>
                    <td className="px-4 py-3">{b.first_name}</td>
                    <td className="px-4 py-3">{b.last_name}</td>
                    <td className="px-4 py-3">{b.phone}</td>
                    <td className="px-4 py-3">{b.email}</td>
                    <td className="px-4 py-3">{b.seats}</td>
                    <td className="px-4 py-3">{b.booking_date}</td>
                    <td className="px-4 py-3">{b.booking_time}</td>
                    <td className="px-4 py-3">{b.special_request || "None"}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-600 transition hover:bg-red-200"
                        onClick={() => openDeleteModal(b)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalConfig && (
        <Modal
          title={modalConfig.title}
          onClose={closeModal}
          showCancel={true}
          showSubmitButton={true}
          submitButtonText={actionLoading ? "Deleting..." : "Delete"}
          onSubmit={async (e) => {
            e.preventDefault();
            if (modalConfig.onConfirm) await modalConfig.onConfirm();
          }}
        >
          {modalConfig.content}
        </Modal>
      )}
    </div>
  );
}